import React from 'react';
import { Plus, Trash2, TrendingUp, TrendingDown, Calculator } from 'lucide-react';
import CustomSelect from '../../ui/CustomSelect';
import { parseRupiah } from '../../hris/recruitment/utils/formatRupiah';

interface PriceSource {
  description?: string;
  source_name?: string;
  value?: number;
  price?: number;
}

interface AppraisalFactor {
  description: string;
  percentage: number;
  value: number;
}

interface AppraisalData {
  method?: string;
  price_sources?: PriceSource[];
  positive_factors?: AppraisalFactor[];
  negative_factors?: AppraisalFactor[];
  pojk_type?: string;
  pojk_percentage?: number;
  liquidation_percentage?: number;
}

interface CollateralAppraisalProps {
  appraisalData: AppraisalData;
  onChange: (data: AppraisalData) => void;
}

// Helper function to calculate average base price
const calculateBasePrice = (sources: PriceSource[]): number => {
  if (!sources || sources.length === 0) return 0;
  const validPrices = sources
    .filter(s => (s.value || s.price || 0) > 0)
    .map(s => parseFloat(String(s.value || s.price || 0)));
  if (validPrices.length === 0) return 0;
  const total = validPrices.reduce((sum, p) => sum + p, 0);
  return total / validPrices.length;
};

// Helper function to format currency
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('id-ID').format(Math.round(value));
};

// Component for Base Price Section
const BasePriceSection: React.FC<{
  method: string;
  priceSources: PriceSource[];
  onMethodChange: (method: string) => void;
  onSourcesChange: (sources: PriceSource[]) => void;
}> = ({ method, priceSources, onMethodChange, onSourcesChange }) => {
  const basePrice = calculateBasePrice(priceSources);

  const addSource = () => {
    onSourcesChange([...priceSources, { description: '', source_name: '', value: 0, price: 0 }]);
  };

  const updateSource = (index: number, field: string, value: any) => {
    const sources = [...priceSources];
    if (field === 'description' || field === 'source_name') {
      sources[index] = {
        ...sources[index],
        description: value,
        source_name: value,
      };
    } else if (field === 'value' || field === 'price') {
      const priceValue = parseFloat(value) || 0;
      sources[index] = {
        ...sources[index],
        value: priceValue,
        price: priceValue,
      };
    }
    onSourcesChange(sources);
  };

  const removeSource = (index: number) => {
    onSourcesChange(priceSources.filter((_, i) => i !== index));
  };

  const getMethodLabel = () => {
    if (method === 'market') return 'Pasar';
    if (method === 'cost') return 'Biaya';
    return 'Pendapatan';
  };

  // Format currency untuk input field
  const formatCurrencyInput = (value: any): string => {
    if (!value && value !== 0) return '';
    const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^\d]/g, '')) : value;
    if (isNaN(numValue)) return '';
    return new Intl.NumberFormat('id-ID').format(numValue);
  };

  // Handle currency change
  const handleCurrencyChange = (index: number, value: string) => {
    const rawValue = parseRupiah(value);
    if (rawValue === '') {
      updateSource(index, 'value', 0);
      return;
    }
    const numValue = parseFloat(rawValue);
    updateSource(index, 'value', isNaN(numValue) ? 0 : numValue);
  };

  return (
    <div className="border rounded-lg p-4 space-y-4">
      <div className="flex justify-between items-start">
        <h4 className="font-semibold text-gray-800">A. Harga Dasar Agunan</h4>
        <div className="text-right">
          <label className="block text-xs text-gray-500 mb-1">Metode Pendekatan Penilaian</label>
          <CustomSelect
            value={method || 'market'}
            onChange={onMethodChange}
            placeholder="Pilih metode"
            width="w-40"
            color="blue"
            options={[
              { value: 'market', label: 'Harga Pasar' },
              { value: 'cost', label: 'Biaya Pengganti' },
              { value: 'income', label: 'Pendapatan' },
            ]}
          />
        </div>
      </div>

      <div className="space-y-3">
        {priceSources.map((source, index) => (
          <div key={index} className="border rounded-lg p-4 animate-imageIn">
            <div className="flex items-center justify-between mb-3">
              <h5 className="font-medium text-gray-700">{index + 1}. Sumber</h5>
              <button
                type="button"
                onClick={() => removeSource(index)}
                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors duration-200"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Sumber</label>
                <input
                  type="text"
                  value={source.description || source.source_name || ''}
                  onChange={e => updateSource(index, 'description', e.target.value)}
                  placeholder="Nama sumber penilaian"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Harga Menurut Sumber
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">Rp</span>
                  <input
                    type="text"
                    value={formatCurrencyInput(source.value || source.price || 0)}
                    onChange={e => handleCurrencyChange(index, e.target.value)}
                    placeholder="0"
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200 text-right"
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={addSource}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50 transition-all duration-300 bg-white"
      >
        <Plus className="w-4 h-4" /> Tambah Sumber Informasi Perhitungan Harga {getMethodLabel()}
      </button>

      {priceSources.length > 0 && (
        <div className="border-t pt-4 mt-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex justify-between items-center">
              <div>
                <h5 className="font-semibold text-blue-900 mb-1">Nilai Agunan</h5>
                <p className="text-sm text-blue-700">Rata-rata dari Sumber Perhitungan</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-blue-700">Rp {formatCurrency(basePrice)}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Component for Factors Section
const FactorsSection: React.FC<{
  title: string;
  icon: React.ReactNode;
  factors: AppraisalFactor[];
  basePrice: number;
  onFactorsChange: (factors: AppraisalFactor[]) => void;
  onAdd: () => void;
  color: 'green' | 'red';
}> = ({ title, icon, factors, basePrice, onFactorsChange, onAdd, color }) => {
  const updateFactor = (index: number, field: string, value: any) => {
    const updatedFactors = [...factors];
    if (field === 'description') {
      updatedFactors[index] = { ...updatedFactors[index], description: value };
    } else if (field === 'percentage') {
      const percentage = parseFloat(value) || 0;
      const calculatedValue = (basePrice * percentage) / 100;
      updatedFactors[index] = {
        ...updatedFactors[index],
        percentage,
        value: calculatedValue,
      };
    }
    onFactorsChange(updatedFactors);
  };

  const removeFactor = (index: number) => {
    onFactorsChange(factors.filter((_, i) => i !== index));
  };

  const total = factors.reduce((sum, f) => sum + (f.value || 0), 0);
  const bgColor = color === 'green' ? 'bg-green-50' : 'bg-red-50';
  const textColor = color === 'green' ? 'text-green-600' : 'text-red-600';
  const placeholder = color === 'green' ? 'Penjelasan faktor positif' : 'Penjelasan faktor negatif';

  return (
    <div className="border rounded-lg p-4 space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="font-semibold text-gray-800 flex items-center gap-2">
          {icon}
          {title}
        </h4>
        <button
          type="button"
          onClick={onAdd}
          className="flex items-center gap-1 px-3 py-1.5 bg-gray-800 text-white text-sm rounded-lg hover:bg-gray-700 transition-colors duration-200"
        >
          <Plus className="w-4 h-4" /> Tambah
        </button>
      </div>

      {factors.length === 0 ? (
        <p className="text-center text-gray-400 py-4">
          Belum ada faktor {color === 'green' ? 'positif' : 'negatif'}
        </p>
      ) : (
        factors.map((factor, index) => (
          <div key={index} className="border rounded-lg p-3 space-y-2 animate-imageIn">
            <div className="flex items-start gap-2">
              <textarea
                placeholder={placeholder}
                value={factor.description || ''}
                onChange={e => updateFactor(index, 'description', e.target.value)}
                rows={2}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
              />
              <button
                type="button"
                onClick={() => removeFactor(index)}
                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors duration-200"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="block text-xs text-gray-600">
                  Persentase {color === 'green' ? 'Penambah' : 'Pengurang'} (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={factor.percentage || ''}
                  onChange={e => updateFactor(index, 'percentage', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
                />
              </div>
              <div className="space-y-1">
                <label className="block text-xs text-gray-600">
                  Nilai {color === 'green' ? 'Penambah' : 'Pengurang'} (Rp)
                </label>
                <input
                  type="text"
                  value={formatCurrency(factor.value || 0)}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-right"
                />
              </div>
            </div>
          </div>
        ))
      )}

      <div className={`flex justify-between items-center p-3 ${bgColor} rounded-lg`}>
        <span className="font-medium text-gray-700">
          Total Faktor {color === 'green' ? 'Positif' : 'Negatif'}:
        </span>
        <span className={`font-bold ${textColor}`}>Rp {formatCurrency(total)}</span>
      </div>
    </div>
  );
};

// Component for Appraisal Result Section
const AppraisalResultSection: React.FC<{
  appraisalData: AppraisalData;
  basePrice: number;
  positiveTotal: number;
  negativeTotal: number;
  onAppraisalDataChange: (data: AppraisalData) => void;
}> = ({ appraisalData, basePrice, positiveTotal, negativeTotal, onAppraisalDataChange }) => {
  const appraisalValue = basePrice + positiveTotal - negativeTotal;
  const pojkValue = (appraisalValue * (appraisalData.pojk_percentage || 0)) / 100;
  const liquidationValue = (appraisalValue * (appraisalData.liquidation_percentage || 80)) / 100;

  const handleChange = (field: string, value: any) => {
    const updatedData = { ...appraisalData };
    if (field === 'liquidation_percentage' || field === 'pojk_percentage') {
      updatedData[field] =
        typeof value === 'string' ? (value === '' ? undefined : parseFloat(value)) : value;
    } else {
      updatedData[field] = value;
    }
    onAppraisalDataChange(updatedData);
  };

  return (
    <div className="border rounded-lg p-4 space-y-4">
      <h4 className="font-semibold text-gray-800 flex items-center gap-2">
        <Calculator className="w-5 h-5 text-blue-500" />
        D. Hasil Taksasi & Likuidasi
      </h4>

      <div className="flex gap-4">
        <div className="flex-1">
          <div className="bg-blue-50 rounded-lg p-4 h-full">
            <p className="text-sm text-gray-600 mb-1">Nilai Taksasi Agunan</p>
            <p className="text-2xl font-bold text-blue-600">Rp {formatCurrency(appraisalValue)}</p>
            <p className="text-xs text-blue-500 mt-1">Harga Dasar + Positif - Negatif</p>
          </div>
        </div>
        <div className="flex-1 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Jenis Agunan untuk POJK
            </label>
            <CustomSelect
              value={appraisalData.pojk_type || ''}
              onChange={v => handleChange('pojk_type', v)}
              placeholder="Pilih jenis"
              width="w-full"
              color="blue"
              options={[
                { value: 'shm', label: 'SHM (Sertifikat Hak Milik)' },
                { value: 'shgb', label: 'SHGB (Sertifikat Hak Guna Bangunan)' },
                { value: 'bpkb', label: 'BPKB Kendaraan' },
                { value: 'deposito', label: 'Deposito / Tabungan' },
                { value: 'other', label: 'Lainnya' },
              ]}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bobot Nilai POJK (%)
            </label>
            <input
              type="number"
              value={appraisalData.pojk_percentage ?? ''}
              onChange={e => {
                const value = e.target.value === '' ? '' : parseFloat(e.target.value) || 0;
                handleChange('pojk_percentage', value);
              }}
              placeholder="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
            />
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200"></div>

      <div className="flex gap-4">
        <div className="flex-1">
          <div className="bg-purple-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Nilai Agunan Menurut POJK</p>
            <p className="text-2xl font-bold text-purple-600">Rp {formatCurrency(pojkValue)}</p>
            <span className="inline-block mt-2 px-2 py-1 bg-purple-200 text-purple-700 text-xs rounded">
              {appraisalData.pojk_percentage || 0}% dari Nilai Taksasi
            </span>
          </div>
        </div>
        <div className="flex-1 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Persentase Likuidasi (%)
            </label>
            <input
              type="number"
              value={appraisalData.liquidation_percentage ?? ''}
              onChange={e => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  handleChange('liquidation_percentage', undefined);
                } else {
                  const numValue = parseFloat(inputValue);
                  if (!isNaN(numValue)) {
                    handleChange('liquidation_percentage', numValue);
                  }
                }
              }}
              onBlur={e => {
                if (
                  e.target.value === '' ||
                  e.target.value === null ||
                  e.target.value === undefined
                ) {
                  handleChange('liquidation_percentage', 80);
                }
              }}
              placeholder="80"
              step="0.01"
              min="0"
              max="100"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
            />
          </div>
          <div className="bg-amber-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Nilai Likuidasi Agunan</p>
            <p className="text-2xl font-bold text-amber-600">
              Rp {formatCurrency(liquidationValue)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Component
const CollateralAppraisal: React.FC<CollateralAppraisalProps> = ({ appraisalData, onChange }) => {
  const data = appraisalData || {};
  const priceSources = data.price_sources || [];
  const positiveFactors = data.positive_factors || [];
  const negativeFactors = data.negative_factors || [];

  const basePrice = calculateBasePrice(priceSources);
  const positiveTotal = positiveFactors.reduce((sum, f) => sum + (f.value || 0), 0);
  const negativeTotal = negativeFactors.reduce((sum, f) => sum + (f.value || 0), 0);

  const handleMethodChange = (method: string) => {
    onChange({ ...data, method });
  };

  const handleSourcesChange = (sources: PriceSource[]) => {
    onChange({ ...data, price_sources: sources });
  };

  const handlePositiveFactorsChange = (factors: AppraisalFactor[]) => {
    onChange({ ...data, positive_factors: factors });
  };

  const handleNegativeFactorsChange = (factors: AppraisalFactor[]) => {
    onChange({ ...data, negative_factors: factors });
  };

  const addPositiveFactor = () => {
    handlePositiveFactorsChange([...positiveFactors, { description: '', percentage: 0, value: 0 }]);
  };

  const addNegativeFactor = () => {
    handleNegativeFactorsChange([...negativeFactors, { description: '', percentage: 0, value: 0 }]);
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-green-600">Taksasi Agunan</h3>

      <BasePriceSection
        method={data.method || 'market'}
        priceSources={priceSources}
        onMethodChange={handleMethodChange}
        onSourcesChange={handleSourcesChange}
      />

      <FactorsSection
        title="B. Faktor Positif (Penambah Nilai)"
        icon={<TrendingUp className="w-5 h-5 text-green-500" />}
        factors={positiveFactors}
        basePrice={basePrice}
        onFactorsChange={handlePositiveFactorsChange}
        onAdd={addPositiveFactor}
        color="green"
      />

      <FactorsSection
        title="C. Faktor Negatif (Pengurang Nilai)"
        icon={<TrendingDown className="w-5 h-5 text-red-500" />}
        factors={negativeFactors}
        basePrice={basePrice}
        onFactorsChange={handleNegativeFactorsChange}
        onAdd={addNegativeFactor}
        color="red"
      />

      <AppraisalResultSection
        appraisalData={data}
        basePrice={basePrice}
        positiveTotal={positiveTotal}
        negativeTotal={negativeTotal}
        onAppraisalDataChange={onChange}
      />
    </div>
  );
};

export default CollateralAppraisal;
